// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, PropsWithChildren } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@store/MobiusOverrides/Link/Link";
import { checkoutProps } from "@store/Themes/common/props/checkout";
import getColor from "@insite/mobius/utilities/getColor";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";

const enum fields {
    showAddressHeader = "showAddressHeader",
}

export interface RequestQuoteAddressesProps extends WidgetProps {
    fields: {
        [fields.showAddressHeader]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    currentBillTo: getCurrentBillToState(state).value,
    currentShipTo: getCurrentShipToState(state).value,
    changeCustomerPageUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
});

type Props = RequestQuoteAddressesProps & ReturnType<typeof mapStateToProps> & HasHistory;

export interface RequestQuoteAddressesStyles {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    container?: GridContainerProps;
    addressItem?: GridItemProps;
    addressContent?: InjectableCss;
    addressHeading?: TypographyProps;
    addressText?: TypographyProps;
    address?: AddressInfoDisplayStyles;
}

export const requestQuoteAddressesStyles: RequestQuoteAddressesStyles = {
    wrapper: checkoutProps.contentWrapper,
    title: {
        css: css`
            display: block;
        `,
    },
    description: {
        css: css`
            display: block;
        `,
    },
    container: {
        gap: 25,
        css: css`
            margin-top: ${pxToRem(20)};
        `,
    },
    addressItem: {
        width: [12, 6, 6, 6, 6],
    },
    addressContent: {
        ...checkoutProps.contentWrapper,
        css: css`
            ${checkoutProps.contentWrapper?.css}
            width: 100%;
            height: 100%;
        `,
    },
    addressHeading: {
        variant: "h5",
        forwardAs: "h2",
        css: css`
            margin-bottom: 0;
        `,
    },
    addressText: {
        css: css`
            display: block;
        `,
    },
    address: {
        wrapper: {
            css: css`
                margin-top: ${pxToRem(15)};
            `,
        },
        customerNameText: {
            css: css`
                font-weight: ${x => x.theme.design.typography.weight.semibold};
            `,
        },
        emailText: {
            css: css`
                text-decoration: underline;
                color: ${getColor("primary")};
            `,
        },
    },
};

const styles = requestQuoteAddressesStyles;

const RequestQuoteAddresses: FC<PropsWithChildren<Props>> = ({
    currentBillTo,
    currentShipTo,
    fields: { showAddressHeader },
    changeCustomerPageUrl,
}) => {
    return (
        <StyledWrapper {...(showAddressHeader ? styles.wrapper : null)}>
            {showAddressHeader && (
                <>
                    <Typography {...styles.addressHeading}>{translate("Addresses")}</Typography>
                    <Typography {...styles.addressText}>
                        {translate("If you want to make changes, you will be redirected to your account")}
                    </Typography>
                    {changeCustomerPageUrl && <Link href={changeCustomerPageUrl}>{translate("Change")}</Link>}
                </>
            )}
            <GridContainer {...styles.container}>
                {currentBillTo && (
                    <GridItem {...styles.addressItem}>
                        <StyledWrapper {...styles.addressContent}>
                            <Typography {...styles.addressHeading}>{translate("Billing Address")}</Typography>
                            <AddressInfoDisplay
                                firstName={currentBillTo.firstName}
                                lastName={currentBillTo.lastName}
                                companyName={currentBillTo.companyName}
                                attention={currentBillTo.attention}
                                address1={currentBillTo.address1}
                                address2={currentBillTo.address2}
                                address3={currentBillTo.address3}
                                address4={currentBillTo.address4}
                                city={currentBillTo.city}
                                state={currentBillTo.state ? currentBillTo.state.abbreviation : undefined}
                                postalCode={currentBillTo.postalCode}
                                country={currentBillTo.country ? currentBillTo.country.abbreviation : undefined}
                                phone={currentBillTo.phone}
                                fax={currentBillTo.fax}
                                email={currentBillTo.email}
                                extendedStyles={styles.address}
                            />
                        </StyledWrapper>
                    </GridItem>
                )}
                {currentShipTo && (
                    <GridItem {...styles.addressItem}>
                        <StyledWrapper {...styles.addressContent}>
                            <Typography {...styles.addressHeading}>{translate("Shipping Address")}</Typography>
                            <AddressInfoDisplay
                                firstName={currentShipTo.firstName}
                                lastName={currentShipTo.lastName}
                                companyName={currentShipTo.companyName}
                                attention={currentShipTo.attention}
                                address1={currentShipTo.address1}
                                address2={currentShipTo.address2}
                                address3={currentShipTo.address3}
                                address4={currentShipTo.address4}
                                city={currentShipTo.city}
                                state={currentShipTo.state ? currentShipTo.state.abbreviation : undefined}
                                postalCode={currentShipTo.postalCode}
                                country={currentShipTo.country ? currentShipTo.country.abbreviation : undefined}
                                phone={currentShipTo.phone}
                                fax={currentShipTo.fax}
                                email={currentShipTo.email}
                                extendedStyles={styles.address}
                            />
                        </StyledWrapper>
                    </GridItem>
                )}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(RequestQuoteAddresses)),
    definition: {
        group: "RFQ Request Quote",
        displayName: "Request Quote Addresses",
        fieldDefinitions: [
            {
                name: fields.showAddressHeader,
                displayName: "Show Address Header",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: true,
            },
        ],
    },
};

export default widgetModule;
